import React, { useEffect, useMemo, useRef } from 'react';
import VideoSphere from './VideoSphere';
import { browserName } from 'react-device-detect';

/**
 *
 * @category Background
 *
 * @module VideoEquirectangular
 *
 * @property {string} backgroundUrl - The video url.
 * @property {Object} videoBackgroundOptions - The video options.
 * @property {boolean} videoBackgroundOptions.loop - The video loop.
 * @property {boolean} videoBackgroundOptions.muted - The video muted.
 * @property {boolean} videoBackgroundOptions.autoplay - The video autoplay.
 * @property {THREE.Scene} scene - The scene where the video will be added.
 * @property {function} onBackgroundLoaded - Callback function when the video is loaded.
 * @property {function} onBackgroundReady - Callback function when the video is ready to play.
 * @property {function} onBackgroundError - Callback function when the video has an error.
 * @returns {boolean}
 *
 * @description
 * This React component renders a video background in the scene. </br>
 *
 *
 * @example
 * import VideoEquirectangular from '@3jss/three-background/VideoRectangular';
 *
 * const MyComponent = () => {
 * 	const scene = new THREE.Scene();
 *
 * 	return (
 * 		<VideoEquirectangular
 * 			backgroundUrl="https://my.video.url"
 * 			videoBackgroundOptions={{
 * 				loop: true,
 * 				muted: true,
 * 				autoplay: true,
 * 			}
 * 			scene={scene}
 * 			onBackgroundLoaded={() => console.log('Video loaded')}
 * 			onBackgroundReady={() => console.log('Video ready')}
 * 			onBackgroundError={() => console.log('Video error')}
 * 		/>
 *  )
 *
 * }
 *
 */
const VideoEquirectangular = ({
	backgroundUrl,
	videoBackgroundOptions,
	scene,
	onBackgroundLoaded,
	onBackgroundReady,
	onBackgroundError,
	onVideoEnd,
	isXREnabled,
}) => {
	const video360 = useRef();

	/**
	 * @description
	 * This object contains the video options.
	 */
	const videoOptions = useMemo(() => {
		return {
			...videoBackgroundOptions,
			backgroundUrl,
		};
	}, [backgroundUrl, videoBackgroundOptions]);

	/**
	 * @description
	 * This function is called when the video is playing.
	 * It adds the video to the scene.
	 * It calls the onBackgroundReady function.
	 * It calls the onBackgroundLoaded function.
	 * @returns {void}
	 */
	const onPlaying = () => {
		scene.add(video360.current);
		onBackgroundReady(true);
		onBackgroundLoaded(true);
	};

	const handlePlayStateEvent = (e) => {
		if (e.detail.playState) {
			video360.current.videoEl.play();
		} else {
			video360.current.videoEl.pause();
		}
	};

	const handleSeekEvent = (e) => {
		const { seekTime } = e.detail;
		video360.current.videoEl.currentTime = seekTime;
	};

	const handleToggleMuteEvent = (e) => {
		video360.current.setMutedConfig(!video360.current.videoEl.muted);
	};

	const addEventListeners = () => {
		window.addEventListener('updateVideoPlayState', handlePlayStateEvent);
		window.addEventListener('onVideoSeekEvent', handleSeekEvent);
		window.addEventListener('toggleMuteEvent', handleToggleMuteEvent);
	};

	const removeEventListeners = () => {
		window.removeEventListener(
			'updateVideoPlayState',
			handlePlayStateEvent,
		);
		window.removeEventListener('onVideoSeekEvent', handleSeekEvent);
		window.removeEventListener('toggleMuteEvent', handleToggleMuteEvent);
	};

	const onTimeUpdate = (currentTime) => {
		const event = new CustomEvent('onVideoTimeUpdateEvent', {
			detail: {
				currentTime,
				duration: video360.current.videoEl.duration,
			},
		});
		window.dispatchEvent(event);
	};
	const onCaptionCuesChange = (val) => {
		const event = new CustomEvent('on360VideoCaptionChange', {
			detail: {
				text: val,
			},
		});
		window.dispatchEvent(event);
	};
	useEffect(() => {
		if (video360.current?.cues?.length > 0) {
			let cues = video360.current?.cues;
			Object.keys(cues).map((key) => {
				let cue = cues.getCueById(key);
				if (cue) {
					cue.onenter = (e) => {
						onCaptionCuesChange(e?.currentTarget?.text);
					};
					cue.onexit = () => onCaptionCuesChange(null);
				}
			});
		}
	}, [video360.current?.cues?.length]);
	useEffect(() => {
		console.log('%c MOUNT VIDEO BG', 'color:green');
		if (backgroundUrl) {
			const segments = isXREnabled
				? browserName === 'Safari'
					? 256
					: 64
				: 128;
			video360.current = new VideoSphere(
				backgroundUrl,
				videoBackgroundOptions,
				onPlaying,
				onBackgroundError,
				segments,
				onTimeUpdate,
				onVideoEnd,
			);
			addEventListeners();
		}
		return () => {
			console.log('%c UNMOUNT VIDEO BG', 'color:red');
			video360.current.dispose();
			scene.remove(video360.current);
			removeEventListeners();
		};
	}, [backgroundUrl, isXREnabled]);

	/**  Loop  **/
	useEffect(() => {
		if (video360.current?.config)
			video360.current.setLoopConfig(videoOptions.loop);
	}, [videoOptions?.loop]);

	/**  Muted  **/
	useEffect(() => {
		if (video360.current?.config)
			video360.current.setMutedConfig(videoOptions.muted);
	}, [videoOptions?.muted]);

	/**  BG  **/
	useEffect(() => {
		console.log('%c CHANGE VIDEO BG', 'color:blue');
		if (backgroundUrl) {
			video360.current.changeTrack(videoOptions.backgroundUrl);
		}
	}, [videoOptions.backgroundUrl]);

	return false;
};

export default VideoEquirectangular;
